import React, { useState } from 'react';
import { Clock, Search, ArrowUp } from 'lucide-react';
import { 
  Button, 
  Card, 
  Checkbox, 
  Container, 
  Divider, 
  Group, 
  List, 
  Modal, 
  TextInput, 
  Text, 
  Title, 
  Box 
} from '@mantine/core';
import { notifications } from '@mantine/notifications';

const AccountDeletion: React.FC = () => {
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deleteText, setDeleteText] = useState('');
  const [understanding, setUnderstanding] = useState(false);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [email, setEmail] = useState('');

  // Handle scroll events for back-to-top button
  React.useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteRequest = () => {
    if (deleteText !== 'DELETE') {
      notifications.show({
        title: 'Confirmation Text Required',
        message: 'Please type DELETE to confirm account deletion',
        color: 'red',
      });
      return;
    }

    if (!email || !email.includes('@')) {
      notifications.show({
        title: 'Valid Email Required',
        message: 'Please enter a valid email address',
        color: 'red',
      });
      return;
    }

    setLoading(true);

    // Simulate API call
    setTimeout(() => {
      setLoading(false);
      setConfirmModalOpen(false);
      setSuccessModalOpen(true);
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-24">
      <div className="container mx-auto px-4 md:px-6 py-12">
        {/* Header */}
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6 font-['Poppins']">
            Account Deletion
          </h1>
          
          <div className="flex items-center gap-4 text-gray-600 mb-8">
            <div className="flex items-center">
              <Clock className="w-5 h-5 mr-2" />
              <span>3 min read</span>
            </div>
            <div className="h-1 w-1 bg-gray-300 rounded-full"></div>
            <span>Last updated: May 8, 2025</span>
          </div>

          {/* Main Content */}
          <div className="space-y-12">
            <section className="bg-white rounded-xl shadow-md p-6 md:p-8">
              <h2 className="text-2xl font-semibold mb-6">Delete Your FeelHeard Account</h2>
              
              <p className="text-gray-700 mb-6">
                At FeelHeard, we respect your right to control your personal data. This page explains how you can
                delete your account and what happens to your data when you do so.
              </p>

              <h3 className="text-xl font-semibold mb-4">What Will Be Deleted:</h3>
              <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
                <li>Your profile information (name, email)</li>
                <li>Your conversation history</li>
                <li>Any questionnaire responses</li>
                <li>Your subscription information and payment methods</li>
              </ul>

              <h3 className="text-xl font-semibold mb-4">What May Be Retained:</h3>
              <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
                <li>Financial records required by tax laws (typically 7 years)</li>
                <li>De-identified, aggregated analytics data that cannot be traced back to you</li>
                <li>Records needed for security and fraud prevention (for 90 days)</li>
              </ul>

              <h3 className="text-xl font-semibold mb-4">Timeline for Deletion</h3>
              <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
                <li>Your account access will be terminated immediately</li>
                <li>Your personal information will be deleted within 30 days</li>
                <li>Your data will be removed from backup systems within 90 days</li>
              </ul>
            </section>

            <section className="bg-white rounded-xl shadow-md p-6 md:p-8">
              <h2 className="text-2xl font-semibold mb-6">Request Account Deletion</h2>
              
              <p className="text-gray-700 mb-6">
                To delete your account, please enter your email address below. We'll send you a verification
                link to confirm your identity and complete the deletion process.
              </p>

              <div className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <TextInput
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your registered email address"
                    className="w-full max-w-md"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-2">
                    Reason for leaving (optional)
                  </label>
                  <TextInput
                    id="reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Help us improve our service"
                    className="w-full max-w-md"
                  />
                </div>

                <Checkbox
                  checked={understanding}
                  onChange={(e) => setUnderstanding(e.target.checked)}
                  label="I understand that deleting my account is permanent and cannot be undone"
                  className="mt-4"
                />

                <div>
                  <Button
                    color="red"
                    onClick={() => setConfirmModalOpen(true)}
                    disabled={!understanding}
                  >
                    Request Account Deletion
                  </Button>
                </div>
              </div>
            </section>

            <section className="bg-white rounded-xl shadow-md p-6 md:p-8">
              <h2 className="text-2xl font-semibold mb-6">Additional Information</h2>
              
              <p className="text-gray-700 mb-4">
                If you have any questions about account deletion or data retention, please contact us at:
              </p>
              
              <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
                <li>Email: <a href="mailto:privacy@somatopia.com" className="text-[#C2A4D6] hover:text-[#B390C9]">privacy@somatopia.com</a></li>
                <li>Support form: <a href="https://feelheard.me/support" className="text-[#C2A4D6] hover:text-[#B390C9]">https://feelheard.me/support</a></li>
              </ul>
              
              <p className="text-gray-700">
                For more information about how we handle your data, please refer to our 
                <a href="/privacy" className="text-[#C2A4D6] hover:text-[#B390C9] mx-1">Privacy Policy</a>.
              </p>
            </section>
          </div>
        </div>
      </div>

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 bg-[#C2A4D6] hover:bg-[#B390C9] text-white p-3 rounded-full shadow-lg transition-colors"
          aria-label="Back to top"
        >
          <ArrowUp className="w-6 h-6" />
        </button>
      )}

      {/* Confirmation Modal */}
      <Modal 
        opened={confirmModalOpen} 
        onClose={() => setConfirmModalOpen(false)}
        title={<Text fw={700} c="red">Final Confirmation</Text>}
        centered
      >
        <Text size="sm" mb="lg">
          This action is permanent. All your personal data will be deleted according to our data retention policy.
        </Text>

        <Text fw={500} mb="xs">To confirm, please type "DELETE" in the field below:</Text>
        <TextInput
          value={deleteText}
          onChange={(e) => setDeleteText(e.target.value)}
          placeholder="Type DELETE"
          mb="md"
        />

        <Group justify="flex-end" gap="md">
          <Button variant="outline" onClick={() => setConfirmModalOpen(false)}>
            Cancel
          </Button>
          <Button 
            color="red" 
            onClick={handleDeleteRequest} 
            loading={loading}
          >
            Delete My Account
          </Button>
        </Group>
      </Modal>

      {/* Success Modal */}
      <Modal
        opened={successModalOpen}
        onClose={() => setSuccessModalOpen(false)}
        title={<Text fw={700}>Account Deletion Requested</Text>}
        centered
      >
        <Text mb="md">
          Your account deletion request has been received. We've sent a verification email to {email}.
        </Text>
        
        <Text size="sm" mb="xl">
          Please check your inbox and click the verification link to complete the deletion process. Your personal data will be 
          deleted from our systems according to our data retention policy.
        </Text>
        
        <Group justify="center">
          <Button variant="outline" onClick={() => setSuccessModalOpen(false)}>
            Close
          </Button>
        </Group>
      </Modal>
    </div>
  );
};

export default AccountDeletion;